import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ArrowLeft, RefreshCw, Truck, FileText, Upload } from "lucide-react"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  DateTimePicker,
  Label,
  ListPageSkeleton,
  ModernButton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Toast,
  ToastType,
  Modal as CustomDialog,
  ShadcnButton,
  Alert,
} from "../../components/ui"
import { DocumentUpload, type EntityDocument } from "../../components/documents/DocumentUpload"
import { DocumentList } from "../../components/documents"
import { cn } from "../../lib/utils"
import type { TFunction } from "i18next"
import { apiService } from "../../services/api"
import type {
 SaleOperationalActivityEvent,
 SalePreparationDetail,
} from "../../types"

function formatDurationMs(ms: number): string {
 const totalSec = Math.max(0, Math.floor(ms / 1000))
 const m = Math.floor(totalSec / 60)
 const h = Math.floor(m / 60)
 const d = Math.floor(h / 24)
 if (d > 0) return `${d}d ${h % 24}h`
 if (h > 0) return `${h}h ${m % 60}m`
 if (m > 0) return `${m}m`
 return `${totalSec}s`
}

function prepStageLabel(key: string, t: TFunction): string {
 if (!key) return "—"
 return String(t(`financialSales.prepStages.${key}`, { defaultValue: key }))
}

function opStageLabel(key: string, t: TFunction): string {
 if (!key) return "—"
 return String(t(`operation.stages.${key}`, { defaultValue: key }))
}

function OperationalTimelineEntry({
 event,
 t,
}: {
 event: SaleOperationalActivityEvent
 t: TFunction
}) {
 const time = new Date(event.occurredAt).toLocaleString()
 const p = event.payload
 if (event.type === "operational_comment") {
  const body = typeof p.body === "string" ? p.body : ""
  const authorName = typeof p.authorName === "string" ? p.authorName : "—"
  return (
   <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-1.5">
    <div className="text-xs text-muted-foreground">{time}</div>
    <div className="text-sm font-medium text-foreground">{authorName}</div>
    <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{body}</p>
   </div>
  )
 }
 const actor = typeof p.changedByName === "string" ? p.changedByName : ""
 const meta = (p.metadata ?? {}) as Record<string, unknown>
 if (event.type === "preparation_kanban_change") {
  const fromTitle = typeof meta.fromTitle === "string" ? meta.fromTitle : "—"
  const toTitle = typeof meta.toTitle === "string" ? meta.toTitle : "—"
  return (
   <div className="border-l-2 border-primary/40 pl-3 py-2">
    <div className="text-xs text-muted-foreground mb-1">{time}</div>
    <p className="text-sm text-foreground">
     {t("operation.timeline.kanbanMoved", { actor, from: fromTitle, to: toTitle })}
    </p>
   </div>
  )
 }
 if (event.type === "operational_stage_change") {
  const from = typeof meta.from === "string" ? opStageLabel(meta.from, t) : "—"
  const to = typeof meta.to === "string" ? opStageLabel(meta.to, t) : "—"
  return (
   <div className="border-l-2 border-primary/40 pl-3 py-2">
    <div className="text-xs text-muted-foreground mb-1">{time}</div>
    <p className="text-sm text-foreground">
     {t("operation.timeline.operationalStage", { actor, from, to })}
    </p>
   </div>
  )
 }
 if (event.type === "preparation_stage_change") {
  const fromRaw = typeof meta.from === "string" ? meta.from : ""
  const toRaw = typeof meta.to === "string" ? meta.to : ""
  const from = prepStageLabel(fromRaw, t)
  const to = prepStageLabel(toRaw, t)
  return (
   <div className="border-l-2 border-primary/40 pl-3 py-2">
    <div className="text-xs text-muted-foreground mb-1">{time}</div>
    <p className="text-sm text-foreground">
     {t("operation.timeline.preparationStage", { actor, from, to })}
    </p>
   </div>
  )
 }
 if (event.type === "checklist_item_change") {
  const title = typeof meta.title === "string" ? meta.title : ""
  const actionRaw = typeof meta.action === "string" ? meta.action : ""
  let actionLabel = actionRaw
  if (actionRaw === "completed") {
   actionLabel = String(t("operation.timeline.checklistActionCompleted"))
  } else if (actionRaw === "reopened") {
   actionLabel = String(t("operation.timeline.checklistActionReopened"))
  } else if (actionRaw === "notes_updated") {
   actionLabel = String(t("operation.timeline.checklistActionNotes"))
  }
  return (
   <div className="border-l-2 border-muted-foreground/30 pl-3 py-2">
    <div className="text-xs text-muted-foreground mb-1">{time}</div>
    <p className="text-sm text-foreground">
     {t("operation.timeline.checklist", { actor, title, action: actionLabel })}
    </p>
   </div>
  )
 }
 return (
  <div className="border-l-2 border-muted pl-3 py-2">
   <div className="text-xs text-muted-foreground mb-1">{time}</div>
   <p className="text-sm text-muted-foreground">{t("operation.timeline.unknownEvent")}</p>
  </div>
 )
}

export const OperationPreparationVehicleDetailPage: React.FC = () => {
 const { t } = useTranslation()
 const navigate = useNavigate()
 const { saleId } = useParams<{ saleId: string }>()
 const [detail, setDetail] = useState<SalePreparationDetail | null>(null)
const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
  const [checklistDraft, setChecklistDraft] = useState<Record<string, boolean>>({})
  const [newCommentText, setNewCommentText] = useState("")
  const [nowTick, setNowTick] = useState(0)
  const [deliveryAt, setDeliveryAt] = useState<string | null>(null)
  const [initialDeliveryAt, setInitialDeliveryAt] = useState<string | null>(null)
  const [showConfirmDelivery, setShowConfirmDelivery] = useState(false)
  const [documents, setDocuments] = useState<EntityDocument[]>([])
  const [loadingDocuments, setLoadingDocuments] = useState(false)

 const load = useCallback(async () => {
  if (!saleId) {
   setLoading(false)
   setDetail(null)
   return
  }
try {
    setLoading(true)
    const detailData = await apiService.getSalePreparationDetail(saleId)
    setDetail(detailData)
    const initialDelivery = detailData.deliveryAt ?? null
    setDeliveryAt(initialDelivery)
    setInitialDeliveryAt(initialDelivery)
    setChecklistDraft(
     Object.fromEntries(
      (detailData.preparationChecklist ?? []).map((item) => [item.id, Boolean(item.completedAt)])
     )
    )
  } catch {
   setDetail(null)
   setToast({ message: t("operation.preparationDetail.loadError"), type: "error" })
  } finally {
   setLoading(false)
  }
}, [saleId, t])

  const loadDocuments = useCallback(async () => {
    if (!saleId) return
    try {
      setLoadingDocuments(true)
      const docs = await apiService.getEntityDocuments("sale", saleId)
      const allDocs = Array.isArray(docs) ? (docs as EntityDocument[]) : []
      const preparationDocs = allDocs.filter((d) => d.type === "preparation")
      setDocuments(preparationDocs)
    } catch {
      setDocuments([])
    } finally {
      setLoadingDocuments(false)
    }
  }, [saleId])

  useEffect(() => {
   void load()
  }, [load])

  useEffect(() => {
    void loadDocuments()
  }, [loadDocuments])

 useEffect(() => {
  const id = window.setInterval(() => setNowTick((n) => n + 1), 30000)
  return () => window.clearInterval(id)
 }, [])

 const vehicleLabel = useMemo(() => {
  const brand = detail?.vehicle?.brand?.name ?? ""
  const model = detail?.vehicle?.model?.name ?? ""
  const version = detail?.vehicle?.version?.name ?? ""
  return [brand, model, version].filter(Boolean).join(" ")
 }, [detail])

 const columnDurationLabel = useMemo(() => {
  void nowTick
  if (!detail?.operationalKanbanColumnEnteredAt) return null
  const start = new Date(detail.operationalKanbanColumnEnteredAt).getTime()
  if (Number.isNaN(start)) return null
  return formatDurationMs(Date.now() - start)
 }, [detail?.operationalKanbanColumnEnteredAt, nowTick])

 const handleChecklistToggle = (itemId: string, completed: boolean) => {
  setChecklistDraft((prev) => ({ ...prev, [itemId]: completed }))
 }

const checklistHasChanges = useMemo(() => {
   if (!detail) return false
   return detail.preparationChecklist.some(
    (item) => Boolean(item.completedAt) !== Boolean(checklistDraft[item.id])
   )
  }, [detail, checklistDraft])

  const formHasChanges = useMemo(() => {
   if (!detail) return false
   const deliveryChanged = deliveryAt !== initialDeliveryAt
   const checklistChanged = detail.preparationChecklist.some(
    (item) => Boolean(item.completedAt) !== Boolean(checklistDraft[item.id])
   )
   return deliveryChanged || checklistChanged
  }, [detail, deliveryAt, initialDeliveryAt, checklistDraft])

 const checklistProgress = useMemo(() => {
  if (!detail) return { done: 0, total: 0, pct: 0 }
  const items = detail.preparationChecklist
  if (items.length > 0) {
   const total = items.length
   const done = items.filter((item) => Boolean(checklistDraft[item.id])).length
   return { done, total, pct: total ? Math.round((done / total) * 100) : 0 }
  }
  const srv = detail.operationalProgress
  if (srv.total > 0) {
   return {
    done: srv.done,
    total: srv.total,
    pct: Math.round((srv.done / srv.total) * 100),
   }
  }
  return { done: 0, total: 0, pct: 0 }
 }, [detail, checklistDraft])

 const handleAddComment = async () => {
  if (!saleId || !detail) return
  const text = newCommentText.trim()
  if (!text) return
  try {
   setSaving(true)
   await apiService.postSaleOperationalComment(saleId, { body: text })
   setNewCommentText("")
   await load()
   setToast({ message: t("operation.timeline.commentAdded"), type: "success" })
  } catch {
   setToast({ message: t("operation.preparationDetail.saveAllError"), type: "error" })
} finally {
    setSaving(false)
   }
  }

  const handleDeliveryAtChange = (newValue: string | null) => {
   setDeliveryAt(newValue)
  }

const handleSaveAll = async () => {
    if (!saleId || !detail || !formHasChanges) return
    try {
     setSaving(true)
     const savePromises: Promise<unknown>[] = []
     if (deliveryAt !== initialDeliveryAt) {
      const kanbanColumnId = detail.operationalKanbanColumn?.id ?? ''
      savePromises.push(apiService.patchSalePreparationKanban(saleId, kanbanColumnId, deliveryAt ?? undefined))
     }
    const changedChecklistItems = detail.preparationChecklist.filter(
     (item) => Boolean(item.completedAt) !== Boolean(checklistDraft[item.id])
    )
    changedChecklistItems.forEach((item) => {
     savePromises.push(
      apiService.patchSaleChecklistItem(saleId, item.id, {
       completed: Boolean(checklistDraft[item.id]),
      })
     )
    })
    await Promise.all(savePromises)
    setInitialDeliveryAt(deliveryAt)
    await load()
    setToast({ message: t("operation.preparationDetail.saveAllSuccess"), type: "success" })
   } catch {
    setToast({ message: t("operation.preparationDetail.saveAllError"), type: "error" })
} finally {
     setSaving(false)
    }
  }

  const handleConfirmDelivery = async () => {
   if (!saleId) return
   try {
    setSaving(true)
    await apiService.deliverSale(saleId)
    setShowConfirmDelivery(false)
    setToast({ message: t("operation.preparationDetail.deliveryConfirmed"), type: "success" })
    await load()
    navigate("/operation/deliveries")
   } catch {
    setToast({ message: t("operation.preparationDetail.deliveryConfirmError"), type: "error" })
   } finally {
    setSaving(false)
   }
  }

  if (loading) {
  return (
   <div className="p-4 md:p-6 max-w-[1200px] mx-auto">
    <ListPageSkeleton />
   </div>
  )
 }

 if (!detail) {
  return <div className="p-4 md:p-6">{t("operation.preparationDetail.notFound")}</div>
 }

 return (
  <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-6">
   {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

   <div className="space-y-1">
    <ModernButton type="button" variant="outline" size="sm" onClick={() => navigate("/operation/preparation")}>
     <ArrowLeft className="h-4 w-4 mr-2" />
     {t("operation.preparationDetail.backToBoard")}
    </ModernButton>
    <h1 className="text-2xl font-bold text-foreground">{t("operation.preparationDetail.title")}</h1>
    <p className="text-sm text-muted-foreground">{t("operation.preparationDetail.subtitle")}</p>
   </div>

<Tabs defaultValue="flow" className="w-full space-y-4">
     <TabsList className="grid w-full grid-cols-3 sm:inline-flex sm:w-auto h-auto sm:h-10 p-1">
      <TabsTrigger value="flow" className="py-2.5 sm:py-1.5">
       {t("operation.preparationDetail.tabFlow")}
      </TabsTrigger>
      <TabsTrigger value="documents" className="py-2.5 sm:py-1.5">
       {t("operation.preparationDetail.tabDocuments")}
      </TabsTrigger>
      <TabsTrigger value="comments" className="py-2.5 sm:py-1.5">
       {t("operation.preparationDetail.tabComments")}
      </TabsTrigger>
     </TabsList>

<TabsContent value="flow" className="space-y-6 mt-4">
    <Card>
     <CardHeader>
      <CardTitle>{t("operation.preparationDetail.vehicleSectionTitle")}</CardTitle>
     </CardHeader>
     <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
      <div>
       <span className="text-muted-foreground">{t("operation.preparationDetail.vehicleLabel")}:</span>{" "}
       <strong>{vehicleLabel || "—"}</strong>
      </div>
      <div>
       <span className="text-muted-foreground">{t("operation.preparationDetail.plate")}:</span>{" "}
       <strong>{detail.vehicle?.identification?.plate || "—"}</strong>
      </div>
      <div>
       <span className="text-muted-foreground">{t("operation.preparationDetail.year")}:</span>{" "}
       <strong>{detail.vehicle?.catalog?.modelYear || detail.vehicle?.modelYear || "—"}</strong>
      </div>
      <div>
       <span className="text-muted-foreground">{t("operation.preparationDetail.mileage")}:</span>{" "}
       <strong>{detail.vehicle?.specifications?.mileage ?? detail.vehicle?.mileage ?? "—"}</strong>
      </div>
      <div>
       <span className="text-muted-foreground">{t("operation.preparationDetail.customer")}:</span>{" "}
       <strong>{detail.customer?.name || "—"}</strong>
      </div>
      <div>
       <span className="text-muted-foreground">{t("operation.preparationDetail.kanbanColumn")}:</span>{" "}
       <strong>{detail.operationalKanbanColumn?.title || "—"}</strong>
      </div>
<div>
        <span className="text-muted-foreground">{t("operation.preparationDetail.deliveryDateLabel")}:</span>{" "}
        <DateTimePicker
         value={deliveryAt}
         onChange={handleDeliveryAtChange}
         disabled={saving}
         placeholder={t("operation.preparationDetail.deliveryDatePlaceholder")}
         minDate={new Date()}
        />
       </div>
      <div>
       <span className="text-muted-foreground">{t("operation.preparationDetail.checklistSectionTitle")}:</span>{" "}
       <strong>
        {checklistProgress.total > 0
         ? t("operation.preparationDetail.checklistProgress", {
            done: checklistProgress.done,
            total: checklistProgress.total,
           })
         : "—"}
       </strong>
      </div>
      <div>
       <span className="text-muted-foreground">{t("operation.preparationDetail.timeInColumn")}:</span>{" "}
       <strong>{columnDurationLabel ?? "—"}</strong>
      </div>
      <div className="md:col-span-2">
      <span className="text-muted-foreground">{t("operation.preparationDetail.columnSince")}:</span>{" "}
      <strong>
       {detail.operationalKanbanColumnEnteredAt
        ? new Date(detail.operationalKanbanColumnEnteredAt).toLocaleString()
        : "—"}
      </strong>
     </div>
    </CardContent>
   </Card>

   <Card>
    <CardHeader>
     <CardTitle className="text-base">
      <span className="block">{t("operation.preparationDetail.checklistSectionTitle")}</span>
      {detail.operationalStage ? (
       <span className="block text-xs font-normal text-muted-foreground mt-0.5">
        {t(`operation.stages.${detail.operationalStage}`, { defaultValue: detail.operationalStage })}
       </span>
      ) : null}
     </CardTitle>
    </CardHeader>
    <CardContent>
     {detail.preparationChecklist.length === 0 ? (
      <p className="text-sm text-muted-foreground">{t("operation.saleDetail.checklistEmpty")}</p>
     ) : (
      <div className="rounded-lg border border-border/80 bg-muted/40 p-4 space-y-4 shadow-sm">
       <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">
         {t("operation.preparationDetail.checklistProgress", {
          done: checklistProgress.done,
          total: checklistProgress.total,
         })}
        </span>
        <span className="tabular-nums font-semibold text-foreground">{checklistProgress.pct}%</span>
       </div>
       <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
         className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
         style={{ width: `${checklistProgress.pct}%` }}
        />
       </div>
       <div className="divide-y divide-border/60 rounded-md border border-border/50 bg-background/80">
        {detail.preparationChecklist.map((item) => {
         const done = Boolean(checklistDraft[item.id])
         return (
          <div
           key={item.id}
           className="flex items-start gap-3 px-3 py-2.5 transition-colors hover:bg-muted/50 first:rounded-t-md last:rounded-b-md"
          >
           <Checkbox
            id={`prep-check-${item.id}`}
            checked={done}
            disabled={saving}
            onCheckedChange={(v) => handleChecklistToggle(item.id, v === true)}
            className="mt-0.5"
           />
           <label
            htmlFor={`prep-check-${item.id}`}
            className={cn(
             "flex-1 cursor-pointer text-sm leading-snug select-none",
             done && "text-muted-foreground line-through"
            )}
           >
            {item.title}
           </label>
          </div>
         )
})}
        </div>
       </div>
      )}
     </CardContent>
</Card>

     </TabsContent>

<TabsContent value="documents" className="space-y-4 mt-4">
       <Card>
        <CardHeader>
         <CardTitle>{t("operation.preparationDetail.documentsTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
         <DocumentUpload
          entityType="sale"
          entityId={saleId || ''}
          saleId={saleId || ''}
          documentType="preparation"
          onUploadSuccess={() => {
           setToast({ message: t("operation.preparationDetail.documentUploaded"), type: "success" })
           void loadDocuments()
          }}
          onUploadError={(msg) => {
           setToast({ message: msg, type: "error" })
          }}
         />
         {loadingDocuments ? (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
         ) : documents.length > 0 ? (
          <DocumentList
           documents={documents}
           readOnly
           title={t("operation.preparationDetail.uploadedDocuments", { count: documents.length })}
           emptyMessage={t("operation.preparationDetail.noDocuments")}
          />
         ) : (
          <p className="text-sm text-muted-foreground">{t("operation.preparationDetail.noDocuments")}</p>
         )}
        </CardContent>
       </Card>
      </TabsContent>

     <TabsContent value="comments" className="space-y-4 mt-4">
     <Card>
      <CardHeader>
       <CardTitle>{t("operation.timeline.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
       <div className="space-y-3 max-h-[min(480px,55vh)] overflow-y-auto pr-1">
        {(detail.operationalActivity?.events ?? []).length === 0 ? (
         <p className="text-sm text-muted-foreground py-2">{t("operation.timeline.empty")}</p>
        ) : (
         (detail.operationalActivity?.events ?? []).map((ev, idx) => (
          <OperationalTimelineEntry
           key={
            ev.type === "operational_comment" && typeof ev.payload.commentId === "string"
             ? ev.payload.commentId
             : `${ev.occurredAt}-${ev.type}-${idx}`
           }
           event={ev}
           t={t}
          />
         ))
        )}
       </div>
       <div className="space-y-3 border-t border-border pt-4">
        <Label className="sr-only" htmlFor="new-operational-comment">
         {t("operation.timeline.newCommentPlaceholder")}
        </Label>
        <Textarea
         id="new-operational-comment"
         value={newCommentText}
         onChange={(e) => setNewCommentText(e.target.value)}
         disabled={saving}
         placeholder={t("operation.timeline.newCommentPlaceholder")}
         className="min-h-[100px]"
        />
        <div className="flex justify-end">
         <ModernButton
          type="button"
          onClick={() => void handleAddComment()}
          disabled={saving || !newCommentText.trim()}
         >
          {t("operation.timeline.addComment")}
         </ModernButton>
        </div>
       </div>
      </CardContent>
     </Card>
    </TabsContent>
   </Tabs>

<div className="mt-10 border-t border-border pt-6 flex flex-wrap items-center justify-between gap-3">
      <div className="flex gap-2">
<ModernButton type="button" variant="outline" onClick={() => void load()} disabled={saving}>
         <RefreshCw className="h-4 w-4 mr-2" />
         {t("operation.refresh")}
        </ModernButton>
{detail.operationalStage === 'preparation' && detail.operationalKanbanColumn?.isHandoffColumn && (
          <ModernButton type="button" variant="warning" onClick={() => setShowConfirmDelivery(true)} disabled={saving}>
           <Truck className="h-4 w-4 mr-2" />
           {t("operation.preparationDetail.confirmDelivery")}
          </ModernButton>
         )}
      </div>
      <ModernButton type="button" onClick={() => void handleSaveAll()} disabled={saving || !formHasChanges}>
       {t("operation.preparationDetail.saveAll")}
      </ModernButton>
     </div>

<CustomDialog
       isOpen={showConfirmDelivery}
       onClose={() => setShowConfirmDelivery(false)}
       title={t("operation.preparationDetail.confirmDeliveryTitle")}
       description={t("operation.preparationDetail.confirmDeliveryDescription")}
       size="sm"
      >
       <div className="space-y-4">
        <Alert type="warning">
         {t("operation.preparationDetail.confirmDeliveryWarning")}
        </Alert>
        <div className="flex justify-end gap-2">
         <ShadcnButton variant="outline" onClick={() => setShowConfirmDelivery(false)} disabled={saving}>
          {t("operation.preparationDetail.confirmDeliveryCancel")}
         </ShadcnButton>
         <ShadcnButton onClick={() => void handleConfirmDelivery()} disabled={saving}>
          {saving ? t("common.processing") : t("operation.preparationDetail.confirmDeliveryConfirm")}
         </ShadcnButton>
        </div>
       </div>
      </CustomDialog>
    </div>
 )
}
